import React, { useMemo, useState } from 'react';
import { Box, Flex, Text, Icon, Button } from '@chakra-ui/react';
import { FiGrid, FiList } from 'react-icons/fi';
import ModalAddSurveyResponse from 'components/Modal/ModalAddSurveyResponse';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';
import { getSurveyResponse } from 'services/surveyService';
import { createLogger } from 'utils/logger';
import FichasDriveView from './FichasDriveView';
import FichasCarpetaView from './FichasCarpetaView';
import FichasListView from './FichasListView';

const logger = createLogger('FichasTab');
const VIEW_MODE_KEY = 'fichas-view-mode';

const FichasTab = ({
  customer, surveys = [], categories = [], updateSurveyResponses, availableSurveys = [],
}) => {
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_MODE_KEY) || 'folders');
  const [activeCategory, setActiveCategory] = useState(null);

  // "Ver ficha" → modal existente, restyleado
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  // "Nueva ficha" → modal existente de creación
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [directAccessForm, setDirectAccessForm] = useState(null);

  const handleSetViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  const handleOpenFicha = (item) => {
    setViewData(null);
    setIsViewOpen(true);
    getSurveyResponse(item.id)
      .then(setViewData)
      .catch(err => {
        logger.error('Error al cargar ficha:', err);
        setViewData({ error: 'No se pudieron cargar los datos de la ficha' });
      });
  };

  const handleCloseView = () => setIsViewOpen(false);

  const handleNewFicha = (category = null) => {
    const forms = category ? availableSurveys.filter(survey => survey.category === category) : [];
    setDirectAccessForm(forms[0] || null);
    setIsAddOpen(true);
  };

  const handleCloseAdd = () => {
    setIsAddOpen(false);
    setDirectAccessForm(null);
  };

  const handleSurveyCreated = () => {
    updateSurveyResponses?.();
    handleCloseAdd();
  };

  const handleSurveyDeleted = () => {
    updateSurveyResponses?.();
    handleCloseView();
  };

  const activeCategoryMeta = useMemo(
    () => categories.find(c => c.slug === activeCategory) || null,
    [categories, activeCategory]
  );

  const surveysInActiveCategory = useMemo(
    () => activeCategory ? surveys.filter(item => (item.survey?.category || 'otros') === activeCategory) : [],
    [surveys, activeCategory]
  );

  return (
    <Box fontFamily="jakarta" p={6}>
      <ModalSurveyResponse
        isOpen={isViewOpen}
        onClose={handleCloseView}
        customer={customer}
        data={viewData}
        onSurveyDeleted={handleSurveyDeleted}
      />

      <ModalAddSurveyResponse
        isOpen={isAddOpen}
        onClose={handleCloseAdd}
        customer={customer}
        updateSurveyResponses={handleSurveyCreated}
        userAuth={customer}
        directAccess={directAccessForm}
      />

      <Flex align="center" justify="space-between" mb="20px" flexWrap="wrap" gap="12px">
        <Flex align="center" gap="10px">
          <Text fontSize="17px" fontWeight="800" color="ink.900" letterSpacing="-0.01em">Fichas</Text>
          <Box as="span" px="9px" py="2px" borderRadius="999px" bg="coral.soft" color="coral.700" fontSize="12px" fontWeight="700">
            {surveys.length} en total
          </Box>
        </Flex>

        <Flex align="center" gap="8px" bg="cream.bg2" border="1px solid" borderColor="cream.line" borderRadius="11px" p="3px">
          <Button size="sm" h="32px" minW="36px" borderRadius="8px" variant={viewMode === 'folders' ? 'solid' : 'ghost'}
            bg={viewMode === 'folders' ? 'white' : 'transparent'} color={viewMode === 'folders' ? 'coral.700' : 'ink.400'}
            boxShadow={viewMode === 'folders' ? '0 1px 2px rgba(54,38,22,.08)' : 'none'}
            onClick={() => handleSetViewMode('folders')} title="Vista de carpetas">
            <Icon as={FiGrid} boxSize="15px" />
          </Button>
          <Button size="sm" h="32px" minW="36px" borderRadius="8px" variant={viewMode === 'list' ? 'solid' : 'ghost'}
            bg={viewMode === 'list' ? 'white' : 'transparent'} color={viewMode === 'list' ? 'coral.700' : 'ink.400'}
            boxShadow={viewMode === 'list' ? '0 1px 2px rgba(54,38,22,.08)' : 'none'}
            onClick={() => handleSetViewMode('list')} title="Vista de lista">
            <Icon as={FiList} boxSize="15px" />
          </Button>
        </Flex>
      </Flex>

      {viewMode === 'folders' ? (
        activeCategory ? (
          <FichasCarpetaView
            category={activeCategoryMeta}
            surveys={surveysInActiveCategory}
            onBack={() => setActiveCategory(null)}
            onOpenFicha={handleOpenFicha}
            onNewFicha={() => handleNewFicha(activeCategory)}
          />
        ) : (
          <FichasDriveView
            categories={categories}
            surveys={surveys}
            onOpenFolder={setActiveCategory}
            onOpenFicha={handleOpenFicha}
          />
        )
      ) : (
        <FichasListView
          categories={categories}
          surveys={surveys}
          customer={customer}
          onNewFicha={() => handleNewFicha(null)}
          onOpenFicha={handleOpenFicha}
        />
      )}
    </Box>
  );
};

export default FichasTab;
